import React, { useCallback, useContext, useEffect, useState } from 'react';
import { View } from 'react-native';
import { useMutation } from '@apollo/client';
import { useForm, FormProvider } from 'react-hook-form';

import { AuthContext } from 'app/src/contexts/AuthContext';
import { Button, ButtonGroup } from 'app/src/elements/buttons';
import { Fields, defaultValues } from 'app/src/elements/hookForms';
import NestedFields from 'app/src/elements/NestedFields';
import MutationErrors from 'app/src/elements/MutationErrors';

import { FIELDS, LINE_FIELDS, EMPTY_LINE } from './new';
import { UPDATE_TXN } from './queries';
import TxnActions from './TxnActions';

import {
  getBalance,
  prepareTxnInput,
  creditOrDebitTxnLine,
} from './utils';

const EditTxn = ({ txn, setShowModal, refetch }) => {
  const { accounts } = useContext(AuthContext);

  const [nestedResources, setNestedResources] = useState(
    txn.txnLines.map(creditOrDebitTxnLine)
  );
  const [formStatus, setFormStatus] = useState();
  const [mutationErrors, setMutationErrors] = useState();

  const {
    register, control, handleSubmit, setValue, watch, formState: { errors }
  } = useForm({
    defaultValues: defaultValues({
      fields: FIELDS,
      resource: txn,
      nestedFields: [{
        nestedResources,
        prefix: 'txnLinesAttributes',
        fields: LINE_FIELDS,
      }],
    })
  });

  const [updateTxn] = useMutation(UPDATE_TXN, {
    onCompleted: data => {
      if (data.updateTxn.errors?.length) {
        setFormStatus(null);
        setMutationErrors(data.updateTxn.errors);
      } else {
        refetch();
        setShowModal(false);
      }
    }
  });

  const onSubmit = useCallback(input => {
    const balance = getBalance({
      accounts,
      txnLines: input.txnLinesAttributes,
    });

    if (balance.abs > 0.0001) {
      setMutationErrors([
        {
          path: ['Transaction lines'],
          message: 'with a posting account must balance to zero ' +
            `(current balance: ${balance})`
        }
      ]);
    } else {
      setFormStatus('submitting');
      updateTxn({ variables: prepareTxnInput(input, FIELDS) });
    }
  });

  const watchAll = watch();

  useEffect(() => {
    const txnLinesWatch = watchAll?.txnLinesAttributes;

    if (txnLinesWatch) {
      const lastLine = txnLinesWatch[txnLinesWatch.length - 1];

      if (lastLine.debitLessCredit) {
        const prefix = `txnLinesAttributes.${nestedResources.length}`;

        register(`${prefix}.accountId`, { value: '' });
        register(`${prefix}.debitLessCredit`, { value: '' });
        register(`${prefix}.memo`, { value: '' });
        setNestedResources([...nestedResources, EMPTY_LINE]);
      }
    }
  }, [watchAll]);

  const lineFields = { ...LINE_FIELDS };

  return (
    <View>
      <TxnActions txn={txn} refetch={refetch} />

      <FormProvider watch={watch}>
        <Fields
          fields={FIELDS}
          control={control}
          errors={errors}
          setValue={setValue}
        />

        <NestedFields
          prefix="txnLinesAttributes"
          resources={nestedResources}
          fields={lineFields}
          register={register}
          control={control}
          errors={errors}
          setValue={setValue}
        />

        <MutationErrors errors={mutationErrors} />

        <ButtonGroup>
          <Button label="Update Transaction" onPress={handleSubmit(onSubmit)} />
        </ButtonGroup>
      </FormProvider>
    </View>
  );
};

export default EditTxn;
